import { inject,
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import * as d3 from "d3";
import { NoDataNotifierService } from 'src/app/services/no-data-notifier.service';

// Config shape mirrors the scraii-ai message.chartConfig contract.
interface ControlChartConfig {
  chart_type?: string;
  chart_variant?: string;
  x_axis?: string;
  y_axis?: string | string[];
  title?: string;
}

// Normalised point used internally for rendering.
interface ControlPoint {
  x: string;
  value: number;
  upper: number | null;
  lower: number | null;
  anomaly: boolean;
  anomalyType?: string | null;
  seriesLabel?: string;
}

// A grouped series (e.g. per source_name) rendered as its own lines + dots.
interface ControlSeries {
  label: string;
  points: ControlPoint[];
}

// Resolved column keys for the active dataset.
interface ResolvedKeys {
  xKey: string;
  valueKey: string;
  upperKey: string | null;
  lowerKey: string | null;
}

// Human-friendly series labels shown in the tooltip and legend.
interface SeriesLabels {
  value: string;
  upper: string;
  lower: string;
}

/**
 * Standalone, self-rendering D3 control chart for the Scraii-AI assistant.
 *
 * Designed to be highly configurable: it reads the x-axis and y-axis keys from
 * the `config` (matching the AI API contract) and infers which series is the
 * measured value vs. the upper / lower control limits. Limits come straight
 * from the API payload (e.g. `hcl` / `lcl`) — no statistical calculation here.
 */
@Component({
    selector: "app-scraii-control-chart",
    templateUrl: "./scraii-control-chart.component.html",
    styleUrls: ["./scraii-control-chart.component.scss"],
    standalone: false
})
export class ScraiiControlChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  private readonly noDataNotifier = inject(NoDataNotifierService);
  @Input() data: any[] | null | undefined = [];
  @Input() config: ControlChartConfig | null | undefined = null;
  @Input() isFullscreen = false;
  @Input() isFloatingView = false;
  @Input() isFloatingExpanded = false;

  // Optional explicit overrides — when omitted, roles are inferred from y_axis.
  @Input() valueKey?: string;
  @Input() upperKey?: string;
  @Input() lowerKey?: string;
  // Optional human-friendly series labels — when omitted, derived dynamically
  // from the resolved keys / chart title (see seriesLabels()).
  @Input() valueLabel?: string;
  @Input() upperLabel?: string;
  @Input() lowerLabel?: string;
  @Input() showBand = true;
  @Input() showLegend = true;
  @Input() colors: {
    value?: string;
    upper?: string;
    lower?: string;
    anomaly?: string;
    band?: string;
  } = {};

  @ViewChild("chartHost", { static: true }) chartHost!: ElementRef<HTMLDivElement>;
  @ViewChild("tooltip", { static: true }) tooltip!: ElementRef<HTMLDivElement>;

  noData = false;

  // Computed once per draw so the mousemove tooltip callback can read labels.
  private activeLabels: SeriesLabels = { value: "Value", upper: "Upper", lower: "Lower" };

  private resizeObserver?: ResizeObserver;
  private resizeRaf: number | null = null;
  private viewReady = false;

  private readonly defaultColors = {
    value: "#8C23E6",
    upper: "#D11044",
    lower: "#1363DF",
    anomaly: "#D11044",
    normal: "#3AC97C",
    band: "#3AC97C",
  };

  private readonly xAxisTitle = "Period";

  // Columns that are never treated as a series / group dimension.
  private readonly reservedRowKeys = new Set([
    "anomaly_type",
    "brand_key",
    "no_of_std_deviations",
    "avg_kpi",
    "stddev_kpi",
  ]);

  // Preferred group-dimension keys when the API adds a breakdown column.
  private readonly preferredGroupKeys = [
    "source_name",
    "source",
    "sp_description",
    "group",
    "series",
    "group_by",
  ];

  ngAfterViewInit(): void {
    this.viewReady = true;
    // Redraw whenever the host box changes (responsive + fullscreen reflow).
    this.resizeObserver = new ResizeObserver(() => this.scheduleDraw());
    this.resizeObserver.observe(this.chartHost.nativeElement);
    this.scheduleDraw();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (
      changes["data"] ||
      changes["config"] ||
      changes["isFullscreen"] ||
      changes["isFloatingView"] ||
      changes["isFloatingExpanded"]
    ) {
      this.scheduleDraw();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeRaf !== null) cancelAnimationFrame(this.resizeRaf);
  }

  private scheduleDraw(): void {
    if (this.resizeRaf !== null) cancelAnimationFrame(this.resizeRaf);
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.draw();
      });
    });
  }

  // ----- key resolution -----------------------------------------------------

  private getYKeys(): string[] {
    const y = this.config?.y_axis;
    if (Array.isArray(y)) return y.filter((k) => typeof k === "string");
    return typeof y === "string" && y.length ? [y] : [];
  }

  private resolveKeys(): ResolvedKeys | null {
    if (!Array.isArray(this.data) || this.data.length === 0) return null;

    // Resolve every key against the keys ACTUALLY present on the data rows.
    // The API's config.x_axis can carry the original SQL column name
    // (e.g. "cal_day") while the result rows emit the x category under the
    // literal key "x_axis" — trusting config blindly collapses every point
    // onto a single x position. Validating against real row keys self-heals
    // that divergence while still honouring explicit overrides / config.
    const rowKeys = Object.keys(this.data[0] ?? {});
    const has = (k?: string | null): k is string =>
      !!k && rowKeys.includes(k);

    const yKeys = this.getYKeys();
    const upperRe = /hcl|ucl|upper/i;
    const lowerRe = /lcl|lower/i;

    // Upper / lower limit keys: override -> y-axis match -> any row match.
    let upperKey: string | null =
      this.upperKey || yKeys.find((k) => upperRe.test(k)) || null;
    if (!has(upperKey)) upperKey = rowKeys.find((k) => upperRe.test(k)) || null;

    let lowerKey: string | null =
      this.lowerKey || yKeys.find((k) => lowerRe.test(k)) || null;
    if (!has(lowerKey)) lowerKey = rowKeys.find((k) => lowerRe.test(k)) || null;

    // Value key: override -> first valid y that isn't a limit -> first row
    // column that isn't x / a limit.
    let valueKey: string | null =
      (has(this.valueKey) && this.valueKey) ||
      yKeys.find((k) => k !== upperKey && k !== lowerKey && has(k)) ||
      null;

    // X key: config key if present on rows -> literal "x_axis" the API emits
    // -> first non-(value/limit) row column.
    let xKey: string | null = this.config?.x_axis ?? null;
    if (!has(xKey)) {
      xKey = has("x_axis")
        ? "x_axis"
        : rowKeys.find(
            (k) => k !== valueKey && k !== upperKey && k !== lowerKey
          ) || null;
    }

    if (!valueKey) {
      valueKey =
        rowKeys.find(
          (k) => k !== xKey && k !== upperKey && k !== lowerKey
        ) || null;
    }

    if (!has(xKey) || !valueKey) return null;
    return { xKey, valueKey, upperKey, lowerKey };
  }

  // Detect an optional grouping column (e.g. source_name) for multi-series payloads.
  // Returns null for single-series charts so existing behaviour is unchanged.
  private resolveGroupKey(keys: ResolvedKeys): string | null {
    if (!Array.isArray(this.data) || this.data.length === 0) return null;

    const rowKeys = Object.keys(this.data[0] ?? {});
    const used = new Set(
      [keys.xKey, keys.valueKey, keys.upperKey, keys.lowerKey]
        .filter((k): k is string => !!k)
    );

    const candidates = rowKeys.filter(
      (k) => !used.has(k) && !this.reservedRowKeys.has(k)
    );

    for (const preferred of this.preferredGroupKeys) {
      if (!candidates.includes(preferred)) continue;
      const unique = new Set(
        this.data.map((row) => row[preferred]).filter((v) => v != null && v !== "")
      );
      if (unique.size > 1) return preferred;
    }

    for (const key of candidates) {
      const sample = this.data[0]?.[key];
      if (typeof sample === "number" || typeof sample === "boolean") continue;
      const unique = new Set(
        this.data.map((row) => row[key]).filter((v) => v != null && v !== "")
      );
      if (unique.size > 1 && unique.size < this.data.length) return key;
    }

    return null;
  }

  // Convert a column key into a human-friendly label. Known control-limit
  // abbreviations are upper-cased; everything else becomes Title Case.
  private formatLabel(key: string): string {
    if (/^(ucl|lcl|hcl|kpi)$/i.test(key)) return key.toUpperCase();
    return key
      .replace(/[_\-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .trim()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }

  // Derive a concise KPI name from the chart title by dropping a trailing
  // "control chart ..." / "for ..." qualifier (e.g.
  // "Discontinued Patients Control Chart for Accredo" -> "Discontinued Patients").
  private kpiNameFromTitle(): string | null {
    const title = (this.config?.title ?? "").trim();
    if (!title) return null;
    const cleaned = title
      .replace(/\s*control\s*chart.*$/i, "")
      .replace(/\s*for\s+.+$/i, "")
      .trim();
    return cleaned.length ? cleaned : title;
  }

  // Resolve display labels for the value / upper / lower series. Explicit
  // @Input labels win; otherwise labels are derived dynamically.
  private seriesLabels(keys: ResolvedKeys): SeriesLabels {
    const value =
      this.valueLabel ||
      this.kpiNameFromTitle() ||
      this.formatLabel(keys.valueKey);
    const upper =
      this.upperLabel ||
      (keys.upperKey ? this.formatLabel(keys.upperKey) : "Upper Limit");
    const lower =
      this.lowerLabel ||
      (keys.lowerKey ? this.formatLabel(keys.lowerKey) : "Lower Limit");
    return { value, upper, lower };
  }

  private toNumber(v: any): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    return isNaN(n) ? null : n;
  }

  private buildPoints(keys: ResolvedKeys, groupKey: string | null): ControlPoint[] {
    if (!Array.isArray(this.data)) return [];

    return this.data
      .map((row) => {
        const value = this.toNumber(row[keys.valueKey]);
        const upper = keys.upperKey ? this.toNumber(row[keys.upperKey]) : null;
        const lower = keys.lowerKey ? this.toNumber(row[keys.lowerKey]) : null;
        if (value === null) return null;

        const anomalyType =
          typeof row.anomaly_type === "string" && row.anomaly_type.trim().length
            ? row.anomaly_type.trim().toLowerCase()
            : null;
        const computedAnomaly =
          (upper !== null && value > upper) ||
          (lower !== null && value < lower);
        const anomaly = anomalyType ? true : computedAnomaly;

        const seriesLabel = groupKey
          ? String(row[groupKey] ?? "").trim() || undefined
          : undefined;

        return {
          x: String(row[keys.xKey] ?? ""),
          value,
          upper,
          lower,
          anomaly,
          anomalyType,
          seriesLabel,
        } as ControlPoint;
      })
      .filter((p): p is ControlPoint => p !== null);
  }

  private buildSeries(keys: ResolvedKeys, groupKey: string | null): ControlSeries[] {
    const points = this.buildPoints(keys, groupKey);
    if (!groupKey) {
      return [{ label: "", points }];
    }

    const xOrder = [...new Set(points.map((p) => p.x))];
    const orderIndex = new Map(xOrder.map((x, i) => [x, i]));
    const groups = new Map<string, ControlPoint[]>();

    points.forEach((point) => {
      const label = point.seriesLabel || "Unknown";
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(point);
    });

    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, seriesPoints]) => ({
        label,
        points: seriesPoints.sort(
          (a, b) => (orderIndex.get(a.x) ?? 0) - (orderIndex.get(b.x) ?? 0)
        ),
      }));
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private formatAnomalyLabel(point: ControlPoint): string {
    if (!point.anomaly) return "Normal";
    if (point.anomalyType) {
      return point.anomalyType.charAt(0).toUpperCase() + point.anomalyType.slice(1);
    }
    return "Anomaly";
  }

  // ----- rendering ----------------------------------------------------------

  private draw(): void {
    const host = this.chartHost?.nativeElement;
    if (!host) return;

    d3.select(host).select("svg").remove();

    const keys = this.resolveKeys();
    const groupKey = keys ? this.resolveGroupKey(keys) : null;
    const seriesList = keys ? this.buildSeries(keys, groupKey) : [];
    const points = seriesList.flatMap((series) => series.points);

    if (!keys || points.length === 0) {
      this.noData = true; this.noDataNotifier.report('scraii-control-chart');
      return;
    }
    this.noData = false;

    const isMultiSeries = !!groupKey && seriesList.length > 1;

    // Resolve display labels once; the tooltip mousemove callback reads them.
    const labels = this.seriesLabels(keys);
    this.activeLabels = labels;

    const c = { ...this.defaultColors, ...this.colors };
    const bounds = host.getBoundingClientRect();
    const measuredWidth = Math.round(bounds.width) || host.clientWidth || 0;
    if (measuredWidth < 10) {
      return;
    }

    const width = Math.max(measuredWidth, 280);
    let height: number;
    if (this.isFloatingView) {
      const aspect = this.isFloatingExpanded ? 2.2 : 1.5;
      height = Math.max(Math.round(width / aspect), 200);
    } else if (this.isFullscreen) {
      height = Math.max(host.clientHeight || 520, 360);
    } else {
      height = Math.max(host.clientHeight || 360, 300);
    }

    // Reserve bottom space for slanted tick labels + the fixed "Period" title.
    const margin = {
      top: 24,
      right: 28,
      bottom: this.isFloatingView ? 76 : 84,
      left: 64,
    };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3
      .select(host)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto")
      .style("display", "block")
      .style("max-width", "100%")
      .style("font-family", "Poppins, sans-serif");

    svg
      .append("defs")
      .append("clipPath")
      .attr("id", "scc-clip")
      .append("rect")
      .attr("width", width)
      .attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)
      .attr("clip-path", "url(#scc-clip)");

    const xScale = d3
      .scalePoint<string>()
      .domain([...new Set(points.map((p) => p.x))])
      .range([0, innerW])
      .padding(0.5);

    const allVals = points.flatMap((p) =>
      [p.value, p.upper, p.lower].filter((v): v is number => v !== null)
    );
    let yMin = d3.min(allVals) ?? 0;
    let yMax = d3.max(allVals) ?? 1;
    const pad = (yMax - yMin) * 0.12 || Math.abs(yMax) * 0.12 || 1;
    yMin = yMin - pad;
    yMax = yMax + pad;

    const yScale = d3.scaleLinear().domain([yMin, yMax]).nice().range([innerH, 0]);

    const seriesColors = isMultiSeries
      ? d3.scaleOrdinal<string, string>(d3.schemeTableau10).domain(
          seriesList.map((series) => series.label)
        )
      : null;

    const lineGen = (accessor: (p: ControlPoint) => number | null) =>
      d3
        .line<ControlPoint>()
        .defined((p) => accessor(p) !== null)
        .x((p) => xScale(p.x) ?? 0)
        .y((p) => yScale(accessor(p) as number))
        .curve(d3.curveMonotoneX);

    // Gridlines (horizontal) — drawn before series so lines render on top.
    g.append("g")
      .attr("class", "scc-grid")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(6)
          .tickSize(-innerW)
          .tickFormat(() => "") as any
      )
      .call((sel) => sel.select(".domain").remove())
      .selectAll("line")
      .attr("stroke", "#E5E7EB")
      .attr("stroke-dasharray", "2,2");

    seriesList.forEach((series) => {
      const seriesColor = isMultiSeries
        ? seriesColors!(series.label)
        : c.value;
      const limitColor = isMultiSeries ? seriesColor : null;

      // Control band (area between upper & lower limits) per series.
      if (this.showBand) {
        const bandPoints = series.points.filter(
          (p) => p.upper !== null && p.lower !== null
        );
        if (bandPoints.length > 1) {
          const area = d3
            .area<ControlPoint>()
            .x((p) => xScale(p.x) ?? 0)
            .y0((p) => yScale(p.lower as number))
            .y1((p) => yScale(p.upper as number))
            .curve(d3.curveMonotoneX);
          g.append("path")
            .datum(bandPoints)
            .attr("fill", isMultiSeries ? seriesColor : c.band)
            .attr("fill-opacity", isMultiSeries ? 0.05 : 0.08)
            .attr("d", area);
        }
      }

      if (series.points.some((p) => p.upper !== null)) {
        g.append("path")
          .datum(series.points)
          .attr("fill", "none")
          .attr("stroke", limitColor ?? c.upper)
          .attr("stroke-width", isMultiSeries ? 1 : 1.5)
          .attr("stroke-opacity", isMultiSeries ? 0.55 : 1)
          .attr("stroke-dasharray", "6,4")
          .attr("d", lineGen((p) => p.upper));
      }

      if (series.points.some((p) => p.lower !== null)) {
        g.append("path")
          .datum(series.points)
          .attr("fill", "none")
          .attr("stroke", limitColor ?? c.lower)
          .attr("stroke-width", isMultiSeries ? 1 : 1.5)
          .attr("stroke-opacity", isMultiSeries ? 0.55 : 1)
          .attr("stroke-dasharray", "6,4")
          .attr("d", lineGen((p) => p.lower));
      }

      g.append("path")
        .datum(series.points)
        .attr("fill", "none")
        .attr("stroke", seriesColor)
        .attr("stroke-width", isMultiSeries ? 1.5 : 2.5)
        .attr("stroke-opacity", isMultiSeries ? 0.85 : 1)
        .attr("d", lineGen((p) => p.value));
    });

    // Axes.
    const xAxis = d3.axisBottom(xScale).tickSizeOuter(0);
    const xAxisG = g
      .append("g")
      .attr("class", "scc-x-axis")
      .attr("transform", `translate(0,${innerH})`)
      .call(xAxis as any)
      .call((sel) => sel.select(".domain").attr("stroke", "#CBD5E1"));

    xAxisG
      .selectAll("text")
      .attr("dy", "0.71em")
      .attr("transform", "rotate(-35)")
      .style("text-anchor", "end")
      .style("font-size", this.isFloatingView ? "10px" : "11px")
      .style("fill", "#475569");

    // Measure how far slanted tick labels extend below the axis line so the
    // "Period" caption sits clear of the data labels.
    const axisBBox = (xAxisG.node() as SVGGElement).getBBox();
    const tickExtent = Math.max(axisBBox.height, this.isFloatingView ? 34 : 38);
    const labelGap = this.isFloatingView ? 10 : 12;
    const axisTitleSize = this.isFloatingView ? 10 : 11;

    g.append("text")
      .attr("class", "scc-x-axis-label")
      .attr("x", innerW / 2)
      .attr("y", innerH + tickExtent + labelGap)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "hanging")
      .style("font-family", "Poppins, sans-serif")
      .style("font-size", `${axisTitleSize}px`)
      .style("font-weight", "600")
      .style("fill", "#6b7280")
      .text(this.xAxisTitle);

    g.append("g")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(6)
          .tickFormat((d) => d3.format("~s")(d as number)) as any
      )
      .call((sel) => sel.select(".domain").attr("stroke", "#CBD5E1"))
      .selectAll("text")
      .style("font-size", "11px")
      .style("fill", "#475569");

    // Data points coloured by status.
    const tooltipEl = this.tooltip.nativeElement;
    g.selectAll(".scc-dot")
      .data(points)
      .enter()
      .append("circle")
      .attr("class", "scc-dot")
      .attr("cx", (p) => xScale(p.x) ?? 0)
      .attr("cy", (p) => yScale(p.value))
      .attr("r", (p) => (p.anomaly ? 6 : isMultiSeries ? 3.5 : 4))
      .attr("fill", (p) => {
        if (p.anomaly) return c.anomaly;
        if (isMultiSeries && p.seriesLabel) return seriesColors!(p.seriesLabel);
        return c.normal;
      })
      .attr("stroke", "#ffffff")
      .attr("stroke-width", (p) => (p.anomaly ? 2 : 1.5))
      .style("cursor", "pointer")
      .on("mousemove", (event: MouseEvent, p: ControlPoint) => {
        this.showTooltip(event, p, c);
      })
      .on("mouseleave", () => {
        d3.select(tooltipEl).style("visibility", "hidden");
      });

    // Legend.
    if (this.showLegend) {
      this.drawLegend(svg, width, margin.top - 14, points, c, labels);
    }
  }

  private drawLegend(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    width: number,
    y: number,
    points: ControlPoint[],
    c: any,
    labels: SeriesLabels
  ): void {
    const items: { label: string; color: string; dashed?: boolean }[] = [
      { label: labels.value, color: c.value },
    ];
    if (points.some((p) => p.upper !== null))
      items.push({ label: labels.upper, color: c.upper, dashed: true });
    if (points.some((p) => p.lower !== null))
      items.push({ label: labels.lower, color: c.lower, dashed: true });
    if (points.some((p) => p.anomaly))
      items.push({ label: "Anomaly", color: c.anomaly });

    const legend = svg.append("g").attr("class", "scc-legend");
    let xCursor = 0;
    const entries: d3.Selection<SVGGElement, unknown, null, undefined>[] = [];
    items.forEach((item) => {
      const entry = legend.append("g");
      entry
        .append("line")
        .attr("x1", 0)
        .attr("x2", 18)
        .attr("y1", 0)
        .attr("y2", 0)
        .attr("stroke", item.color)
        .attr("stroke-width", 2.5)
        .attr("stroke-dasharray", item.dashed ? "5,3" : "0");
      entry
        .append("circle")
        .attr("cx", 9)
        .attr("cy", 0)
        .attr("r", 3.5)
        .attr("fill", item.color);
      entry
        .append("text")
        .attr("x", 24)
        .attr("y", 4)
        .style("font-size", "11px")
        .style("fill", "#374151")
        .text(item.label);
      entries.push(entry);
    });

    // Centre the legend row.
    const gaps = 22;
    const widths = entries.map((e) => (e.node() as SVGGElement).getBBox().width);
    const totalW = widths.reduce((a, b) => a + b, 0) + gaps * (entries.length - 1);
    xCursor = Math.max((width - totalW) / 2, 8);
    entries.forEach((entry, i) => {
      entry.attr("transform", `translate(${xCursor},${y})`);
      xCursor += widths[i] + gaps;
    });
  }

  private showTooltip(event: MouseEvent, p: ControlPoint, c: any): void {
    const host = this.chartHost.nativeElement;
    const tooltipEl = this.tooltip.nativeElement;
    const rect = host.getBoundingClientRect();
    const fmt = (v: number | null) =>
      v === null ? "—" : d3.format(",")(Math.round(v * 100) / 100);

    const statusColor = p.anomaly ? c.anomaly : c.normal;
    const statusLabel = this.formatAnomalyLabel(p);
    const labels = this.activeLabels;

    const row = (color: string, label: string, val: number | null) =>
      `<div class="scc-tip-row"><span class="scc-tip-swatch" style="background:${color}"></span><span class="scc-tip-label">${this.escapeHtml(
        label
      )}</span><span class="scc-tip-val">${fmt(val)}</span></div>`;

    const rows: string[] = [row(c.value, labels.value, p.value)];
    if (p.upper !== null) rows.push(row(c.upper, labels.upper, p.upper));
    if (p.lower !== null) rows.push(row(c.lower, labels.lower, p.lower));

    const seriesHeader = p.seriesLabel
      ? `<div class="scc-tip-series">${this.escapeHtml(p.seriesLabel)}</div>`
      : "";
    const periodHeader = `<div class="scc-tip-period">${this.escapeHtml(p.x)}</div>`;

    const headerContent = p.seriesLabel
      ? `${seriesHeader}${periodHeader}`
      : `<div>${this.escapeHtml(p.x)}</div>`;

    tooltipEl.innerHTML = `
      <div class="scc-tip-header">
        ${headerContent}
      </div>
      <div class="scc-tip-body">
        ${rows.join("")}
        <div class="scc-tip-status">
          <span class="scc-tip-swatch" style="background:${statusColor}"></span>
          <span class="scc-tip-status-label">${this.escapeHtml(statusLabel)}</span>
        </div>
      </div>`;

    // Make visible first so we can measure the rendered size, then position
    // relative to the host with edge-aware flipping/clamping.
    d3.select(tooltipEl).style("visibility", "visible");
    const tipW = tooltipEl.offsetWidth || 190;
    const tipH = tooltipEl.offsetHeight || 0;
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const gap = 16;
    const left =
      offsetX + gap + tipW > rect.width ? offsetX - gap - tipW : offsetX + gap;
    const top = Math.min(Math.max(offsetY - 10, 0), Math.max(rect.height - tipH, 0));
    d3.select(tooltipEl)
      .style("left", `${Math.max(left, 0)}px`)
      .style("top", `${top}px`);
  }
}
